import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { Asset } from '@/types/asset';

interface AssetPDFExportOptions {
  asset: Asset;
  includePhotos?: boolean;
  includeTransactionHistory?: boolean;
}

export function exportAssetDetailToPDF(options: AssetPDFExportOptions): jsPDF {
  const { asset, includePhotos = false } = options;
  
  // Create new PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Add title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Asset Detail Report', 14, 15);

  // Add generation date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 22);

  let yPosition = 32;

  // Asset Header Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Asset Information', 14, yPosition);
  yPosition += 8;

  // Basic Information Table
  const basicInfo = [
    ['Asset Code', asset.code],
    ['Description', asset.name],
    ['Category', asset.category],
    ['Sub-Category', asset.subCategory || '-'],
    ['Location', asset.location],
    ['Sub-Location', asset.subLocation || '-'],
    ['Status', asset.status],
    ['Serial Number', asset.serialNumber || '-'],
    ['Manufacturer', asset.manufacturer || '-'],
    ['Model', asset.model || '-'],
    ['Barcode', asset.barcode || '-'],
  ];

  autoTable(doc, {
    body: basicInfo,
    startY: yPosition,
    theme: 'plain',
    styles: {
      fontSize: 10,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 130 },
    },
    margin: { left: 14, right: 14 },
  });

  yPosition = (doc as any).lastAutoTable.finalY + 10;

  // Financial Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Financial Information', 14, yPosition);
  yPosition += 8;

  const formatCurrency = (value: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  const financialInfo = [
    ['Acquisition Cost', formatCurrency(asset.acquisitionCost, asset.currency)],
    ['Acquisition Date', format(new Date(asset.acquisitionDate), 'dd/MM/yyyy')],
    ['Net Book Value', formatCurrency(asset.netBookValue, asset.currency)],
    ['Accumulated Depreciation', formatCurrency(asset.accumulatedDepreciation, asset.currency)],
    ['Currency', asset.currency],
    ['Depreciation Method', asset.depreciationMethod || '-'],
    ['Useful Life', asset.usefulLife ? `${asset.usefulLife} years` : '-'],
    ['Residual Value', asset.residualValue ? formatCurrency(asset.residualValue, asset.currency) : '-'],
    ['Quantity', asset.quantity?.toString() || '1'],
  ];

  autoTable(doc, {
    body: financialInfo,
    startY: yPosition,
    theme: 'plain',
    styles: {
      fontSize: 10,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 130 },
    },
    margin: { left: 14, right: 14 },
  });

  yPosition = (doc as any).lastAutoTable.finalY + 10;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Assignment Information Section
  if (asset.assignedTo || asset.assignedDate) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Assignment Information', 14, yPosition);
    yPosition += 8;

    const assignmentInfo = [
      ['Assigned To', asset.assignedTo || '-'],
      ['Assigned Date', asset.assignedDate ? format(new Date(asset.assignedDate), 'dd/MM/yyyy') : '-'],
    ];

    autoTable(doc, {
      body: assignmentInfo,
      startY: yPosition,
      theme: 'plain',
      styles: {
        fontSize: 10,
        cellPadding: 2,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 130 },
      },
      margin: { left: 14, right: 14 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Insurance & Warranty Section
  if (asset.insurancePolicyNumber || asset.warrantyStartDate) {
    // Check if we need a new page
    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Insurance & Warranty', 14, yPosition);
    yPosition += 8;

    const insuranceInfo: string[][] = [];
    
    if (asset.insurancePolicyNumber) {
      insuranceInfo.push(
        ['Insurance Policy', asset.insurancePolicyNumber],
        ['Insurance Provider', asset.insuranceProvider || '-'],
        ['Insurance Start Date', asset.insuranceStartDate ? format(new Date(asset.insuranceStartDate), 'dd/MM/yyyy') : '-'],
        ['Insurance Expiry Date', asset.insuranceExpiryDate ? format(new Date(asset.insuranceExpiryDate), 'dd/MM/yyyy') : '-'],
        ['Insurance Value', asset.insuranceValue ? formatCurrency(asset.insuranceValue, asset.currency) : '-']
      );
    }

    if (asset.warrantyStartDate) {
      insuranceInfo.push(
        ['Warranty Start Date', format(new Date(asset.warrantyStartDate), 'dd/MM/yyyy')],
        ['Warranty Expiry Date', asset.warrantyExpiryDate ? format(new Date(asset.warrantyExpiryDate), 'dd/MM/yyyy') : '-'],
        ['Warranty Provider', asset.warrantyProvider || '-'],
        ['Warranty Cost', asset.warrantyCost ? formatCurrency(asset.warrantyCost, asset.currency) : '-']
      );
    }

    if (insuranceInfo.length > 0) {
      autoTable(doc, {
        body: insuranceInfo,
        startY: yPosition,
        theme: 'plain',
        styles: {
          fontSize: 10,
          cellPadding: 2,
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50 },
          1: { cellWidth: 130 },
        },
        margin: { left: 14, right: 14 },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }
  }

  // Supplier Information Section
  if (asset.supplierName || asset.purchaseOrderNumber) {
    // Check if we need a new page
    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Supplier Information', 14, yPosition);
    yPosition += 8;

    const supplierInfo = [
      ['Supplier', asset.supplierName || '-'],
      ['Purchase Order', asset.purchaseOrderNumber || '-'],
      ['Invoice Reference', asset.invoiceReference || '-'],
      ['Hire Purchase', asset.hirePurchase ? 'Yes' : 'No'],
    ];

    autoTable(doc, {
      body: supplierInfo,
      startY: yPosition,
      theme: 'plain',
      styles: {
        fontSize: 10,
        cellPadding: 2,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 130 },
      },
      margin: { left: 14, right: 14 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Costing Information Section
  if (asset.project || asset.costCentre) {
    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Costing Information', 14, yPosition);
    yPosition += 8;

    const costingInfo = [
      ['Project', asset.projectName || asset.project || '-'],
      ['Cost Centre', asset.costCentreName || asset.costCentre || '-'],
      ['Sub Ledger', asset.subLedger || '-'],
    ];

    autoTable(doc, {
      body: costingInfo,
      startY: yPosition,
      theme: 'plain',
      styles: {
        fontSize: 10,
        cellPadding: 2,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 130 },
      },
      margin: { left: 14, right: 14 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Notes Section
  if (asset.notes) {
    // Check if we need a new page
    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes', 14, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const splitNotes = doc.splitTextToSize(asset.notes, 180);
    doc.text(splitNotes, 14, yPosition);
    yPosition += splitNotes.length * 5 + 5;
  }

  // Add page numbers to all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  return doc;
}

export function downloadAssetPDF(doc: jsPDF, assetCode: string): void {
  const filename = `asset-detail-${assetCode}-${format(new Date(), 'yyyyMMdd-HHmmss')}.pdf`;
  doc.save(filename);
}

export function getAssetPDFBlob(doc: jsPDF): Blob {
  return doc.output('blob');
}
